import { useTranslation } from "react-i18next";
import { AudioPreviewButton } from "./AudioPreviewButton";
import type { ReactHookFormInput } from "./Input";

export type DropdownOption = {
  disabled?: boolean;
  value: string;
  optionText: string;
  disabledTooltipText?: string;
  audio_preview_url?: string;
  isHidden?: boolean;
};

export type DropdownOptionGroup = {
  label: string;
  options: DropdownOption[];
};

export interface DropdownProps
  extends React.SelectHTMLAttributes<HTMLSelectElement>,
    ReactHookFormInput {
  asDefaultValue?: boolean;
  label?: string;
  width?: string;
  options: DropdownOption[];
  optionGroups?: DropdownOptionGroup[];
  onClickCb?: React.MouseEventHandler<HTMLSelectElement>;
  onChangeCb?: (value: string, index: number) => void;
  noSelectionText?: string;
  noStatsAvailable?: boolean;
  noStatsAvailableText?: string;
  defaultValueIndex?: number;
  languague?: boolean;
  mobile?: boolean;
  borderOff?: boolean;
  labelFontClassName?: string;
  selectedTextStyle?: React.CSSProperties;
  optionTextStyle?: React.CSSProperties;
  previewButtonClassName?: string;
  allowNoSelectionOption?: boolean;
}

const Dropdown = ({
  label,
  width = "",
  asDefaultValue,
  options,
  optionGroups,
  registerFct,
  onClickCb,
  onChangeCb,
  noSelectionText,
  noStatsAvailable = false,
  noStatsAvailableText,
  defaultValueIndex = -1,
  mobile = false,
  languague = false,
  borderOff = false,
  labelFontClassName,
  selectedTextStyle,
  optionTextStyle,
  previewButtonClassName,
  allowNoSelectionOption,
  ...props
}: DropdownProps) => {
  const { t } = useTranslation();

  const indexZeroText = () => {
    if (noStatsAvailable) {
      return noStatsAvailableText || t("pages.statistics.noStatsAvailable");
    } else if (noSelectionText) {
      return noSelectionText;
    } else {
      return t("general.pleaseSelect");
    }
  };

  const allOptions = optionGroups ? optionGroups.flatMap((g) => g.options) : options;

  const controlDefaultValueIndex = () => {
    if (languague) {
      return defaultValueIndex === 2 ? "2" : "1";
    } else {
      return asDefaultValue
        ? allOptions[defaultValueIndex >= 0 ? defaultValueIndex : -1]?.value
        : 0;
    }
  };

  const defaultValue = allOptions[defaultValueIndex >= 0 ? defaultValueIndex : -1]?.value ?? 0;
  const currentSelectedValue = props.value !== undefined ? props.value : defaultValue;
  const selectedOption = allOptions.find((opt) => opt.value === currentSelectedValue);
  const hasPreview = selectedOption?.audio_preview_url;

  const registerField = registerFct?.();

  const handleChange = (val: React.ChangeEvent<HTMLSelectElement>) => {
    const value = val.target.value;
    const index = val.target.options.selectedIndex;

    if (registerField?.onChange) {
      void registerField.onChange(val);
    }
    if (onChangeCb) {
      onChangeCb(value, index);
    }
  };

  return (
    <div className={`relative ${width === "w-full" ? "w-full" : ""} ${mobile ? "-mt-2" : ""}`}>
      <label
        className={`block font-medium text-gray-700 ${
          mobile ? "text-sm" : labelFontClassName || "text-lg"
        }`}
      >
        {label}
      </label>
      <div className="flex items-center">
        <select
          key={`${controlDefaultValueIndex()}-${mobile}`}
          id={props.id || `dropdown-${label}`}
          className={`${width} ${borderOff ? "border-0" : ""} ${
            noStatsAvailable || props?.disabled ? "cursor-not-allowed" : "cursor-pointer"
          } ${mobile ? "h-2/5" : ""}`}
          defaultValue={defaultValue}
          disabled={noStatsAvailable}
          {...props}
          {...registerField}
          onClick={onClickCb}
          onChange={handleChange}
        >
          {allowNoSelectionOption
            ? !asDefaultValue && <option value={0}>{indexZeroText()}</option>
            : !asDefaultValue &&
              !allOptions.some((opt) => opt.value === "") && (
                <option value="0" disabled={!!currentSelectedValue}>
                  {indexZeroText()}
                </option>
              )}
          {optionGroups
            ? optionGroups.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map((option, i) => {
                    const isSelected = option.value === currentSelectedValue;
                    return (
                      <option
                        key={`${i}-${option.value}`}
                        disabled={option.disabled}
                        value={option.value}
                        title={
                          option.disabled && option.disabledTooltipText
                            ? option.disabledTooltipText
                            : ""
                        }
                        style={
                          option?.isHidden
                            ? { display: "none" }
                            : isSelected
                            ? selectedTextStyle || {}
                            : optionTextStyle || {}
                        }
                      >
                        {option.optionText}
                      </option>
                    );
                  })}
                </optgroup>
              ))
            : options.map((option, i) => {
                const isSelected = option.value === currentSelectedValue;
                return (
                  <option
                    key={`${i}-${option.value}`}
                    disabled={option.disabled}
                    value={option.value}
                    title={
                      option.disabled && option.disabledTooltipText
                        ? option.disabledTooltipText
                        : ""
                    }
                    style={
                      option?.isHidden
                        ? { display: "none" }
                        : isSelected
                        ? selectedTextStyle || {}
                        : optionTextStyle || {}
                    }
                  >
                    {option.optionText}
                  </option>
                );
              })}
        </select>
        {hasPreview && (
          <AudioPreviewButton
            url={hasPreview}
            className={`ml-[6px] ${previewButtonClassName || ""}`}
          />
        )}
      </div>
    </div>
  );
};

export default Dropdown;
